import React, { useState } from 'react';
import { Form, Button, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import dbService from '../../../services/dbService';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import matchingService from '../../../services/matchingService';

const OnboardingScreening = ({ watchlists }) => {
    const entityLists = watchlists.filter(wl => wl.type === 'Entity');
    const [formData, setFormData] = useState({ name: '', nationality: '', dob: '' });
    const [result, setResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    const handleScreening = (e) => {
        e.preventDefault();
        setIsLoading(true);
        setResult(null);

        let highestScore = 0;
        let bestMatch = null;
        let hitList = '';
        
        // --- THIS IS THE CRITICAL FIX (Part 1) ---
        // Declare variables to store the score breakdown of the BEST match found.
        // These exist outside the loop, so they will "survive" after the loops finish.
        let bestMatchDetails = {
            nameScore: 0,
            countryScore: 0,
            dobScore: 0,
        };
        // --- END OF FIX ---

        for (const list of entityLists) {
            for (const entry of list.entries) {
                const nameScore = matchingService.fuzzyMatch(formData.name, entry.name) * 70;
                const countryScore = matchingService.deterministicMatch(formData.nationality, entry.nationality) ? 15 : 0;
                const dobScore = matchingService.deterministicMatch(formData.dob, entry.dob) ? 15 : 0;
                
                const totalScore = nameScore + countryScore + dobScore;

                if (totalScore > highestScore) {
                    highestScore = totalScore;
                    bestMatch = entry;
                    hitList = list.name;
                    
                    // --- THIS IS THE CRITICAL FIX (Part 2) ---
                    // When we find a new best match, we SAVE its score breakdown.
                    bestMatchDetails = { nameScore, countryScore, dobScore };
                }
            }
        }

        setTimeout(() => {
            if (highestScore > 65) {
                // --- THIS IS THE CRITICAL FIX (Part 3) ---
                // Now, we use the saved breakdown from the variables that survived the loops.
                const matchDetails = [
                    { field: 'Name', value: formData.name, matchedOn: bestMatch.name, score: bestMatchDetails.nameScore.toFixed(0) },
                    { field: 'Nationality', value: formData.nationality, matchedOn: bestMatch.nationality, score: bestMatchDetails.countryScore },
                    { field: 'Date of Birth', value: formData.dob, matchedOn: bestMatch.dob, score: bestMatchDetails.dobScore },
                ];
                setResult({ status: 'Match Found', score: highestScore.toFixed(0), list: hitList, details: matchDetails });
            } else {
                setResult({ status: 'Clear', score: 0 });
            }
            dbService.addLog({ user: user.name, action: 'Onboarding Screening', details: `Screened '${formData.name}'. Result: ${highestScore > 65 ? 'Match' : 'Clear'}` });
            setIsLoading(false);
        }, 1000);
    };

    const createCase = async () => {
        const newCase = {
            customerName: formData.name, customerId: `NEW-${Date.now()}`, risk: 'High',
            status: 'Open', assignedTo: 'Compliance', openDate: new Date().toISOString().split('T')[0],
            notes: [{ user: 'System', text: `Case created from Onboarding Screening match on ${result.list}. Score: ${result.score}.`, timestamp: new Date().toISOString() }],
            auditTrail: [], transactions: []
        };
        const newCaseId = await dbService.add('cases', newCase);
        toast.success(`New Case #${newCaseId} created for compliance review.`);
        navigate(`/case-management/${newCaseId}`);
    };

    return (
        <div>
            <p>Screen new individuals or entities against sanction lists before onboarding. The system uses a **fuzzy matching algorithm** to provide a confidence score based on multiple factors.</p>
            <Card>
                <Card.Body>
                    <Form onSubmit={handleScreening}>
                        <Row>
                            <Col md={6}><Form.Group className="mb-3"><Form.Label>Full Name / Entity Name</Form.Label><Form.Control type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required placeholder="e.g. John Smath" /></Form.Group></Col>
                            <Col md={3}><Form.Group className="mb-3"><Form.Label>Nationality (3-letter code)</Form.Label><Form.Control type="text" value={formData.nationality} onChange={e => setFormData({...formData, nationality: e.target.value.toUpperCase()})} placeholder="e.g. SYR" maxLength="3" /></Form.Group></Col>
                            <Col md={3}><Form.Group className="mb-3"><Form.Label>Date of Birth</Form.Label><Form.Control type="date" value={formData.dob} onChange={e => setFormData({...formData, dob: e.target.value})} /></Form.Group></Col>
                        </Row>
                        <Button type="submit" disabled={isLoading}>{isLoading ? <Spinner as="span" size="sm" /> : 'Screen Customer'}</Button>
                    </Form>
                </Card.Body>
            </Card>

            {result && (
                <Alert variant={result.status === 'Match Found' ? 'danger' : 'success'} className="mt-4">
                    <Alert.Heading>{result.status}</Alert.Heading>
                    {result.status === 'Match Found' ? (
                        <>
                            <p>A potential match was found on the <strong>{result.list}</strong> with a total confidence score of <strong>{result.score}%</strong>.</p>
                            <h6>Match Breakdown:</h6>
                            <ul>
                                {result.details.map(d => d.value && <li key={d.field}><strong>{d.field} "{d.value}"</strong> matched on "{d.matchedOn}" (Score contribution: {d.score})</li>)}
                            </ul>
                            <hr />
                            <Button variant="danger" onClick={createCase}>Create Case for Review</Button>
                        </>
                    ) : <p>The customer profile appears to be clear of any strong matches on the monitored lists.</p>}
                </Alert>
            )}
        </div>
    );
};

export default OnboardingScreening;